import type { CampaignTaskDomain } from './model';

export const CAMPAIGN_ADMIN_SCHEMA_VERSION = 1 as const;

export type CampaignTaskTypeSettingChange =
  | { kind: 'llmReview' | 'llmValidator' | 'claimingPaused'; enabled: boolean }
  | { kind: 'domains'; value: CampaignTaskDomain[] }
  | { kind: 'domainClaimingPaused'; domainId: string; enabled: boolean };

export interface UpdateCampaignTaskTypeSettingsCommand {
  schemaVersion: typeof CAMPAIGN_ADMIN_SCHEMA_VERSION;
  campaignId: string;
  taskTypeId: string;
  changes: CampaignTaskTypeSettingChange[];
  dryRun?: boolean;
}

export interface CampaignSettingIssue {
  index: number;
  kind: string;
  code: string;
  message: string;
}

export interface UpdateCampaignTaskTypeSettingsResult {
  schemaVersion: typeof CAMPAIGN_ADMIN_SCHEMA_VERSION;
  dryRun: boolean;
  applied: Array<{ index: number; kind: string }>;
  rejected: CampaignSettingIssue[];
  changed: boolean;
}

export interface AdjustCampaignTaskPoolCommand {
  schemaVersion: typeof CAMPAIGN_ADMIN_SCHEMA_VERSION;
  campaignId: string;
  taskTypeId: string;
  targetCount: number;
  domainId?: string | null;
  domainLabel?: string | null;
  dryRun?: boolean;
}

export interface AdjustCampaignTaskPoolResult {
  schemaVersion: typeof CAMPAIGN_ADMIN_SCHEMA_VERSION;
  dryRun: boolean;
  created: number;
  assigned?: number;
  deleted: number;
  totalAfter: number;
  availableAfter: number;
}

export interface CampaignAdminGateway {
  updateTaskTypeSettings(
    command: UpdateCampaignTaskTypeSettingsCommand,
  ): Promise<UpdateCampaignTaskTypeSettingsResult>;
  adjustTaskPool(
    command: AdjustCampaignTaskPoolCommand,
  ): Promise<AdjustCampaignTaskPoolResult>;
}

export interface CampaignAdminApi {
  updateTaskTypeSettings(
    input: Omit<UpdateCampaignTaskTypeSettingsCommand, 'schemaVersion'>,
  ): Promise<UpdateCampaignTaskTypeSettingsResult>;
  previewTaskTypeSettings(
    input: Omit<UpdateCampaignTaskTypeSettingsCommand, 'schemaVersion' | 'dryRun'>,
  ): Promise<UpdateCampaignTaskTypeSettingsResult>;
  adjustTaskPool(
    input: Omit<AdjustCampaignTaskPoolCommand, 'schemaVersion'>,
  ): Promise<AdjustCampaignTaskPoolResult>;
  previewTaskPool(
    input: Omit<AdjustCampaignTaskPoolCommand, 'schemaVersion' | 'dryRun'>,
  ): Promise<AdjustCampaignTaskPoolResult>;
}

const KEBAB = /^[a-z][a-z0-9-]*$/;

export function validateTaskTypeSettingsCommand(
  input: Omit<UpdateCampaignTaskTypeSettingsCommand, 'schemaVersion'>,
): string | null {
  if (!input.campaignId.trim()) return 'campaignId is required.';
  if (!input.taskTypeId.trim() || !KEBAB.test(input.taskTypeId.trim())) {
    return 'taskTypeId must be kebab-case.';
  }
  if (!Array.isArray(input.changes) || input.changes.length === 0) {
    return 'At least one setting change is required.';
  }
  if (input.changes.length > 20) return 'A maximum of 20 setting changes may be submitted at once.';
  return null;
}

export function validateTaskPoolCommand(
  input: Omit<AdjustCampaignTaskPoolCommand, 'schemaVersion'>,
): string | null {
  if (!input.campaignId.trim()) return 'campaignId is required.';
  if (!input.taskTypeId.trim() || !KEBAB.test(input.taskTypeId.trim())) {
    return 'taskTypeId must be kebab-case.';
  }
  if (!Number.isInteger(input.targetCount) || input.targetCount < 0 || input.targetCount > 1000) {
    return 'targetCount must be an integer in [0, 1000].';
  }
  return null;
}

export function createCampaignAdminApi(gateway: CampaignAdminGateway): CampaignAdminApi {
  const send = async (
    input: Omit<UpdateCampaignTaskTypeSettingsCommand, 'schemaVersion'>,
    dryRun: boolean,
  ) => {
    const normalized = {
      ...input,
      campaignId: input.campaignId.trim(),
      taskTypeId: input.taskTypeId.trim(),
      dryRun,
      schemaVersion: CAMPAIGN_ADMIN_SCHEMA_VERSION,
    } as UpdateCampaignTaskTypeSettingsCommand;
    const error = validateTaskTypeSettingsCommand(normalized);
    if (error) throw new Error(error);
    return gateway.updateTaskTypeSettings(normalized);
  };

  const adjust = async (
    input: Omit<AdjustCampaignTaskPoolCommand, 'schemaVersion'>,
    dryRun: boolean,
  ) => {
    const normalized = {
      ...input,
      campaignId: input.campaignId.trim(),
      taskTypeId: input.taskTypeId.trim(),
      dryRun,
      schemaVersion: CAMPAIGN_ADMIN_SCHEMA_VERSION,
    } as AdjustCampaignTaskPoolCommand;
    const error = validateTaskPoolCommand(normalized);
    if (error) throw new Error(error);
    return gateway.adjustTaskPool(normalized);
  };

  return {
    updateTaskTypeSettings: (input) => send(input, input.dryRun === true),
    previewTaskTypeSettings: (input) => send(input, true),
    adjustTaskPool: (input) => adjust(input, input.dryRun === true),
    previewTaskPool: (input) => adjust(input, true),
  };
}
